<template>
  <c-loader-status
    :loaders="{
      'no-initial-content no-loading-content no-secondary-progress': [
        service.getSeating,
      ],
    }"
  >
    <seat-map-render
      v-if="(!isSoftJournMap || !seatingInfo) && context"
      class="purchase-seat-map can-pan"
      :context="context"
    >
    </seat-map-render>
  </c-loader-status>
  <sj-seat-map-render
    v-if="isSoftJournMap && seatingInfo"
    mode="picker"
    :seatMapId="seatingInfo.seatMap!.id"
    :venueId="seatingInfo.seatMap!.venueId"
    :eventDateId="seatingInfo.eventDateId ?? undefined"
    :reservationId="props.purchase.publicId ?? undefined"
    ref="sjSeatMapRenderRef"
    @tickets-change="sjOnTicketsChange"
    :showTicketTypesTab="true"
  />
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from "vue";
import {
  PublicPurchaseServiceViewModel,
  TicketPurchaseDtoViewModel,
} from "@public/viewmodels.g";
import {
  setupPurchaseReloadHandling,
  suppressPurchaseScroll,
} from "@common/utils";

import { PurchaseSeatMapContext } from "@common/seat-map-context";
import SeatMapRender from "@common/components/seat-map-render.vue";
import SjSeatMapRender from "@common/components/sj-seat-map-render.vue";
import { SeatMapMethodEnum } from "@/models.g";

const props = defineProps<{
  purchase: TicketPurchaseDtoViewModel;
  context: PurchaseSeatMapContext | null;
  service: PublicPurchaseServiceViewModel;
}>();

props.service.getSeating.setConcurrency("debounce");

const context = ref<PurchaseSeatMapContext | null>(props.context);
const isSoftJournMap = ref(false);
const seatingInfo =
  ref<ReturnType<typeof props.service.getSeating>["result"]>(null);
const sjSeatMapRenderRef = ref<InstanceType<typeof SjSeatMapRender> | null>(
  null,
);

const emit = defineEmits<{
  "context-updated": [context: PurchaseSeatMapContext];
}>();

// Watch for context changes and emit them
watch(
  context,
  (newContext: PurchaseSeatMapContext) => {
    emit("context-updated", newContext);
  },
  { immediate: true },
);

const endpoints = [props.service.addTicket, props.service.removeTicket];
//this will effectively reload the ticket purchase data when one of the endpoints are called
// ensuring that the reactive tickets in the shopping cart are updated.
for (const endpoint of endpoints) {
  setupPurchaseReloadHandling(props.purchase, endpoint, ...endpoints);
}

props.service.getSeating.onFulfilled(async (res) => {
  if (res.wasSuccessful) {
    seatingInfo.value = props.service.getSeating.result;
    isSoftJournMap.value =
      props.service.getSeating.result?.seatMap?.method ===
      SeatMapMethodEnum.SoftJourn;

    const priceSelectCallback = () => {};

    const takenSeatInfoCallback = () => {
      return;
    };

    context.value = new PurchaseSeatMapContext(
      props.service.getSeating.result!,
      props.purchase,
      props.service,
      priceSelectCallback,
      takenSeatInfoCallback,
      nextTick,
    );
  }
});
props.service.getSeating(props.purchase.publicId);

//watches for changes that come from the shopping cart and reflect them here.
watch(
  () => props.purchase.tickets,
  () => {
    if (!context.value) {
      return;
    }
    context.value.selectedObjects = new Set(
      context.value.allSeats.filter((seat) =>
        props.purchase.tickets?.some((t) => t.seatNumber == seat.seatNumber),
      ),
    );
  },
  { deep: true },
);

//signals the SoftJourn widget to refresh its own cart display, but only when
//the GA ticket composition actually changed — avoids refreshing on unrelated
//(e.g. seated-ticket-only) purchase.tickets changes.
function refreshSJCartOnGaChanges(isLoadingSource: () => boolean) {
  watch(isLoadingSource, (newValue, oldValue) => {
    if (!newValue && oldValue) {
      sjSeatMapRenderRef.value?.pushRefreshCartSignal();
    }
  });
}

refreshSJCartOnGaChanges(() => props.service.addMultipleTickets.isLoading);
refreshSJCartOnGaChanges(() => props.service.removeMultipleTickets.isLoading);

async function sjOnTicketsChange() {
  if (!props.purchase.$load.isLoading) {
    suppressPurchaseScroll.value = true; // #3470
    await props.purchase.$load(props.purchase.publicId ?? undefined);
  }
  return { ok: true };
}
</script>

<style lang="scss">
.purchase-seat-map {
  .seat-map-scroll-frame {
    max-height: min(40vh, 400px) !important;
  }

  .seat {
    cursor: pointer;

    &.original-seat {
      border-style: dotted;
      border-color: black;
    }
  }

  .annotation:hover::before {
    opacity: 0 !important;
  }
}
</style>
